import { createContext, useReducer } from 'react';
import type { ReactElement, ReactNode } from 'react';

import type { ISubscription } from 'models/Subscription';
import FetchState from 'models/FetchState';

type Props = { children: ReactNode; };
type Dispatcher = (action: Action) => void;

type Action =
  | { type: 'reload'; data: ISubscription; }
  | { type: 'setFetchState'; data: FetchState; };

type ContextData = {
  subscription: ISubscription;
  fetchState: FetchState;
};

type ContextValue = ContextData & { dispatch: Dispatcher };

const SubscriptionContext = createContext<ContextValue | null>(null);

function subscriptionReducer(
  value: ContextValue,
  action: Action,
): ContextData {
  const { subscription, fetchState } = value;

  switch (action.type) {
    case 'reload': {
      return {
        fetchState,
        subscription: action.data,
      };
    }

    case 'setFetchState': {
      return {
        subscription,
        fetchState: action.data,
      };
    }

    default: {
      return value;
    }
  }
}

function SubscriptionProvider({ children }: Props): ReactElement {
  const [value, dispatch] = useReducer(subscriptionReducer, {
    fetchState: FetchState.IN_PROGRESS,
    subscription: {} as ISubscription,
  });

  const { subscription, fetchState } = value;

  return (
    <SubscriptionContext.Provider value={{ subscription, dispatch, fetchState }}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export type { ContextValue };
export { SubscriptionContext, SubscriptionProvider };
